import { Link } from 'react-router-dom';
import TrustBadges from '../components/TrustBadges';
import JsonLd from '../components/JsonLd';

const SENDER_STEPS = [
  {
    n: 1,
    title: 'Verify your work email',
    body: `Enter your office email — we send you a one-time 6-digit code. No password to manage, no IT ticket. You accept the BAA once and you're set up for every future transfer.`
  },
  {
    n: 2,
    title: 'Drop in your files',
    body: `Drag a CBCT, panoramic, intraoral scan, DICOM study, PDF, or photo into the upload area. Up to 10 GB per file. Multiple files per transfer. Uploads are encrypted in transit.`,
    // The step's unanswered question is "how do I get the DICOM out of my
    // imaging software in the first place?" — which our CBCT guide answers,
    // per vendor. Linking makes the two pages complementary rather than two
    // of ours chasing one query.
    more: {
      href: 'https://intake.dental/how-to-send-a-cbct-scan',
      label: 'How to export a CBCT from Romexis, Sidexis, CS 3D and others →'
    }
  },
  {
    n: 3,
    title: 'Add the recipient and set a PIN',
    body: `Type the specialist's email (or several). Set a 4-digit PIN. We email them a secure link — but we never email the PIN. You text or call them the PIN separately.`
  },
  {
    n: 4,
    title: 'Specialist opens the package',
    body: `They click the link, enter the PIN, and download the package. The link auto-expires after 7 days. You get a notification when files are accessed.`
  },
];

const RECIPIENT_FAQ = [
  {
    q: 'Got an email from us — what do I do?',
    a: `Click the link, enter the 4-digit PIN the sender texted you, and download. The PIN is in a different message than the email by design — that's how we keep PHI out of inboxes.`
  },
  {
    q: 'I never got the PIN.',
    a: `The sender texts or calls it to you separately — it is never in the email. Contact the sender directly.`
  },
  {
    q: 'The link says "expired."',
    a: `Links auto-delete after 7 days. Ask the sender to re-send.`
  },
];

// Exported so scripts/prerender.mjs can emit it into the static <head> at build
// time. The <JsonLd> component below injects it from a useEffect, which never
// runs during prerendering — and only ever put it in the DOM after JS.
export const HOWTO_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to send dental files securely with Intake.Dental Send',
  description: 'A four-step HIPAA-compliant flow to send x-rays, DICOM studies, and patient records to a dental specialist.',
  totalTime: 'PT1M',
  estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
  tool: [{ '@type': 'HowToTool', name: 'Web browser' }],
  step: SENDER_STEPS.map(s => ({
    '@type': 'HowToStep',
    position: s.n,
    name: s.title,
    text: s.body,
    url: `https://send.intake.dental/how-it-works#step-${s.n}`,
  })),
};

export default function HowItWorksPage() {
  return (
    <div className="landing">
      <JsonLd id="howto" data={HOWTO_SCHEMA} />
      <section className="hero">
        <div className="eyebrow">How it works</div>
        <h1>Send dental files in under a minute.</h1>
        <p className="hero-lead">
          A four-step flow built around the way dentists and specialists actually share x-rays,
          DICOM studies, and patient records. No accounts. No software. No PHI in the inbox.
        </p>
        <TrustBadges />
      </section>

      <section className="content-section">
        <h2 className="section-title">For senders</h2>
        <div className="step-list">
          {SENDER_STEPS.map(s => (
            <div key={s.n} className="detail-step">
              <div className="detail-step-num">{s.n}</div>
              <div className="detail-step-body">
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                {s.more && (
                  <p style={{ marginTop: 6 }}>
                    <a href={s.more.href}>{s.more.label}</a>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="callout">
          <strong>Why text the PIN instead of emailing it?</strong>
          <p>
            Email isn't a secure channel for PHI access credentials. Splitting the link
            (email) from the unlock code (text or call) keeps a single intercepted inbox
            from being enough to open patient files — that's the practical core of HIPAA's
            access-control rule.
          </p>
        </div>
      </section>

      <section className="content-section">
        <h2 className="section-title">For recipients</h2>
        <div className="qa-list">
          {RECIPIENT_FAQ.map((item, i) => (
            <div key={i} className="qa-item">
              <h4>{item.q}</h4>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready to send?</h2>
        <p className="muted">It's free. No credit card. No software install.</p>
        <Link to="/" className="btn btn-primary btn-large">Send a file now</Link>
      </section>
    </div>
  );
}
